import React, { useState, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import { Course } from "../types/courseTypes";
import { addCourse, updateCourse } from "../services/MentorApiService";
import { useSnackbar } from './DashBoard';

interface ConversationModalProps {
    open: boolean;
    onClose: () => void;
    onCreate: (convId: number) => void;
    course?: Course | null;
}

const CourseDialog: React.FC<ConversationModalProps> = ({ open, onClose, onCreate, course }) => {
    const [courseName, setCourseName] = useState<string>("");
    const [courseDepartment, setCourseDepartment] = useState<string>("");
    const [courseNameError, setCourseNameError] = useState<string>("");
    const [courseDepartmentError, setCourseDepartmentError] = useState<string>("");
    const { showSnackbar } = useSnackbar();

    const resetValues = () => {
        setCourseName("");
        setCourseDepartment("");
        setCourseNameError("");
        setCourseDepartmentError("");
    };

    useEffect(() => {
        if (course) {
            setCourseName(course.courseName);
            setCourseDepartment(course.courseDepartment);
            setCourseNameError("");
            setCourseDepartmentError("");
        } else {
            resetValues();
        }
    }, [course]);

    const validateInputs = (): boolean => {
        let isValid = true;

        if (courseName.length < 3) {
            setCourseNameError("Course title must be at least 3 characters");
            isValid = false;
        } else if (courseName.length > 64) {
            setCourseNameError("Course title cannot exceed 64 characters");
            isValid = false;
        } else {
            setCourseNameError("");
        }
        if (courseDepartment.length < 3) {
            setCourseDepartmentError("Department must be at least 3 characters");
            isValid = false;
        } else if (courseDepartment.length > 64) {
            setCourseDepartmentError("Department cannot exceed 64 characters");
            isValid = false;
        } else {
            setCourseDepartmentError("");
        }

        return isValid;
    };

    const sendCreateRequest = async (params: any) => {
        addCourse(params)
            .then((response) => {
                showSnackbar('New course successfully added', 'success');
                onCreate(response.data.conversationId);
            })
            .catch((error) => {
                showSnackbar('Failed to create new course', 'error');
            });
    };

    const sendUpdateRequest = async (params: any) => {
        if (!course) return;
        updateCourse(course.id, params)
            .then((response) => {
                showSnackbar('Course has been successfully updated', 'info');
                onClose();
            })
            .catch((error) => {
                showSnackbar('Failed to update course', 'error');
            });
    };

    const handleSubmit = async () => {
        if (!validateInputs()) {
            return;
        }
        const params = {
            courseName: courseName,
            courseDepartment: courseDepartment,
        };
        resetValues();
        if (course) {
            await sendUpdateRequest(params);
        } else {
            await sendCreateRequest(params);
        }
    };

    const handleModalClose = () => {
        resetValues();
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{course ? "Edit Course" : "Add Course"}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Course Title"
                    fullWidth
                    variant="outlined"
                    value={courseName}
                    onChange={(e) => setCourseName(e.target.value)}
                    error={!!courseNameError}
                    helperText={courseNameError}
                />
                <TextField
                    margin="dense"
                    label="Course Department or School"
                    fullWidth
                    variant="outlined"
                    value={courseDepartment}
                    onChange={(e) => setCourseDepartment(e.target.value)}
                    error={!!courseDepartmentError}
                    helperText={courseDepartmentError}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleModalClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} color="primary">
                    {course ? "Update Course" : "Add Course"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CourseDialog;